import { useCallback, useState } from 'react'

import styled, { css } from 'styled-components'

import config from '@config'
import translate from '@i18n'
import useAuthentication from '@hooks/useAuthentication'
import useFeatureFlagsSkeleton from '@hooks/useFeatureFlagsSkeleton'
import useIsAuthResolved from '@hooks/useIsAuthResolved'
import useTracking from '@hooks/useTracking'
import useUser from '@hooks/useUser'
import { CookWidget, JSONTypeForCookWidget } from '@widgets/types'
import {
  agbClickHandler,
  ctaLogoutClickHandler,
  dataProtectionClickHandler,
  dataProtectionPolicyClickHandler,
  myTeamsClickHandler,
  profileClickHandler,
  subscriptionManagementClickHandler,
} from '@widgets/UserProfile/tracking'
import SecondaryCTAButton from '@components/Buttons/SecondaryCTAButton'
import FollowMyTeamDrawer from '@components/FollowMyTeam/FollowMyTeamDrawer'
import UserProfileComponents from '@components/UserProfilePage'

const {
  auth: { myServicesUrl },
  publication: { publication },
  gdpr: { dataPrivacyUrl, dataPrivacyPolicyUrl, agbUrl },
} = config

const {
  Wrapper,
  OnboardingMessage,
  ContentList,
  ContentListItem,
  ThemeSwitcher,
  LeserreporterEntryPoint,
} = UserProfileComponents

const StyledSecondaryCTAButton = styled(SecondaryCTAButton)`
  width: 100%;
`

const SettingsWrapper = styled.div`
  ${({
    theme: {
      spacing: { spacing8 },
    },
  }) => css`
    display: flex;
    flex-direction: column;
    gap: ${spacing8};
    width: 100%;
    box-sizing: border-box;
  `}
`

const UserProfilePage = () => {
  const user = useUser()
  const { logout, visitUserAccount } = useAuthentication()
  const isAuthResolved = useIsAuthResolved()
  const { isFollowMyTeamEnabled } = useFeatureFlagsSkeleton()
  const [isFollowMyTeamDrawerOpen, setIsFollowMyTeamDrawerOpen] =
    useState(false)

  const userName = user
    ? user.email_only
      ? user.email
      : `${user.first_name} ${user.last_name}`
    : ''

  const handleLogoutTrack = useTracking(ctaLogoutClickHandler)
  const onLogout = useCallback(() => {
    handleLogoutTrack()
    logout()
  }, [logout, handleLogoutTrack])

  const handleProfileTrack = useTracking(profileClickHandler)
  const onProfile = useCallback(() => {
    handleProfileTrack()
    visitUserAccount()
  }, [handleProfileTrack, visitUserAccount])

  const handleSubscriptionManagementTrack = useTracking(
    subscriptionManagementClickHandler,
  )
  const onSubscriptionManagement = useCallback(() => {
    handleSubscriptionManagementTrack()
    window.open(myServicesUrl, '_blank', 'noopener')
  }, [handleSubscriptionManagementTrack])

  const handleMyTeamsTrack = useTracking(myTeamsClickHandler)
  const showMyTeams = useCallback(() => {
    handleMyTeamsTrack()
    setIsFollowMyTeamDrawerOpen(true)
  }, [handleMyTeamsTrack])

  const handleDataProtectionTrack = useTracking(dataProtectionClickHandler)
  const onDataProtection = useCallback(
    () => handleDataProtectionTrack(),
    [handleDataProtectionTrack],
  )
  const handleDataProtectionPolicyTrack = useTracking(
    dataProtectionPolicyClickHandler,
  )
  const onDataProtectionPolicy = useCallback(
    () => handleDataProtectionPolicyTrack(),
    [handleDataProtectionPolicyTrack],
  )

  const handleAGB = useTracking(agbClickHandler)
  const onAGB = useCallback(() => handleAGB(), [handleAGB])
  const closeFollowMyTeamDrawer = () => {
    setIsFollowMyTeamDrawerOpen(false)
  }

  return (
    isAuthResolved && (
      <Wrapper>
        {!user && <OnboardingMessage />}
        {publication !== 'romandie' && <LeserreporterEntryPoint />}
        {!!user && (
          <>
            <ContentList title={translate('userProfilePage.profileListTitle')}>
              <ContentListItem
                onClick={onProfile}
                name={userName}
                iconName={'sign-in'}
                type={'button'}
                shouldShowSquareIcon
              />
              {publication !== 'romandie' && (
                <ContentListItem
                  onClick={onSubscriptionManagement}
                  name={translate('userProfilePage.myServices')}
                  iconName="arrows-rotate"
                  type="button"
                  shouldShowSquareIcon
                />
              )}
            </ContentList>

            <SettingsWrapper>
              <ContentList title={translate('userProfilePage.settingsTitle')}>
                {publication !== 'romandie' && (
                  <ContentListItem
                    name={translate('userProfilePage.myTeams')}
                    iconName="soccer"
                    type="button"
                    onClick={showMyTeams}
                  />
                )}
                <ContentListItem
                  name={translate('userProfilePage.darkMode')}
                  iconName="brightness"
                  type="text"
                />
              </ContentList>
              <ThemeSwitcher />
            </SettingsWrapper>
          </>
        )}
        <ContentList title={translate('userProfilePage.aboutUsTitle')}>
          <ContentListItem
            name={translate('userProfilePage.dataProtection')}
            iconName={'shield-keyhole'}
            link={{
              href: dataPrivacyUrl,
              ariaLabel: translate('config.gdprTitle'),
            }}
            onClick={onDataProtection}
            type="link"
          />
          <ContentListItem
            name={translate('userProfilePage.privacyPolicy')}
            iconName="file-shield"
            link={{
              href: dataPrivacyPolicyUrl,
            }}
            onClick={onDataProtectionPolicy}
            type="link"
          />
          <ContentListItem
            name={translate('userProfilePage.agb')}
            iconName="memo-circle-check"
            link={{ href: agbUrl }}
            onClick={onAGB}
            type="link"
          />
        </ContentList>
        {!!user && (
          <StyledSecondaryCTAButton
            onClick={onLogout}
            type="button"
            size="large"
            iconName={'arrow-right-from-bracket'}
            colorVariant="red"
          >
            {translate('userProfilePage.logout')}
          </StyledSecondaryCTAButton>
        )}
        {isFollowMyTeamEnabled && (
          <FollowMyTeamDrawer
            openAt={isFollowMyTeamDrawerOpen ? 'showMyTeams' : undefined}
            onClose={closeFollowMyTeamDrawer}
          />
        )}
      </Wrapper>
    )
  )
}

const widget = {
  kind: ['user-profile-page'],
  component: UserProfilePage,
} as const satisfies CookWidget

export type WidgetType = typeof widget

export type JSONWidgetType = JSONTypeForCookWidget<WidgetType>

export default widget
